import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { usersApi } from '@/api/users';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert } from '@/components/ui/alert';

export default function Settings() {
  const { user, accessibleCompanies } = useAuthStore();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: '新しいパスワードが一致しません' });
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'パスワードは8文字以上で入力してください' });
      return;
    }

    setIsSubmitting(true);

    try {
      await usersApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      setMessage({ type: 'success', text: 'パスワードを変更しました' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'パスワードの変更に失敗しました',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleLabel = user?.role === 'super_admin' ? 'スーパー管理者' : '法人管理者';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">アカウント設定</h1>
        <p className="text-sm sm:text-base text-gray-500">アカウント情報を管理します</p>
      </div>

      {/* プロフィール情報 */}
      <Card>
        <CardHeader>
          <CardTitle>プロフィール情報</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-primary flex items-center justify-center text-white text-2xl font-bold">
                {user?.full_name?.charAt(0) || 'U'}
              </div>
              <div>
                <p className="text-lg font-semibold">
                  {user?.role === 'super_admin'
                    ? user?.full_name
                    : accessibleCompanies[0]?.display_name || accessibleCompanies[0]?.name || user?.full_name}
                </p>
                <p className="text-gray-500">{user?.email}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <p className="text-sm text-gray-500">権限</p>
                <p className="font-medium">{roleLabel}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">認証方法</p>
                <p className="font-medium">
                  {user?.auth_provider === 'google' ? 'Google認証' : 'メール認証'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* パスワード変更（メール認証ユーザーのみ） */}
      {user?.auth_provider === 'email' && (
        <Card>
          <CardHeader>
            <CardTitle>パスワード変更</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
              {message && (
                <Alert
                  variant={message.type === 'success' ? 'success' : 'error'}
                  onClose={() => setMessage(null)}
                >
                  {message.text}
                </Alert>
              )}

              <Input
                label="現在のパスワード"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
              <Input
                label="新しいパスワード"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                helperText="8文字以上で入力してください"
                required
              />
              <Input
                label="新しいパスワード（確認）"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <Button type="submit" isLoading={isSubmitting}>
                パスワードを変更
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* セッション情報 */}
      <Card>
        <CardHeader>
          <CardTitle>セッション情報</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div>
              <p className="text-sm text-gray-500">作成日</p>
              <p className="font-medium">
                {user?.created_at
                  ? new Date(user.created_at).toLocaleDateString('ja-JP')
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">最終ログイン</p>
              <p className="font-medium">
                {user?.last_login_at
                  ? new Date(user.last_login_at).toLocaleString('ja-JP')
                  : '-'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
